import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AgencyFilter } from 'src/app/models/AgencyFilter';
import { Notification } from 'src/app/models/Notification';
import { PartnerInfo } from 'src/app/models/PartnerInfo';
import { Twilio } from 'src/app/models/Twilio';
import { User } from 'src/app/models/User';
import { UserEditLog } from 'src/app/models/UserEditLog';
import { UserPayment } from 'src/app/models/UserPayment';
import { TwoFactorProvision } from 'src/app/models/Token';
import { environment } from 'src/environments/environment';

import { AffiliatesPage } from '../../models/Affiliate';
import { UserCommission } from '../../models/UserCommission';
import { UserFilter } from './../../models/UserFilter';
import { PaginatedResult } from './../model/paginated-result';
import { AuthService } from './auth.service';
import * as ApiToken from 'src/app/models/ApiToken';

export const ADMINISTRATOR = 'c7eedac4-47bc-4262-b6e3-7d242c27449f';
export const AGENCY_CLIENT = '8fe4e83a-db75-45f0-bf78-4a5e830c8101';
export const CLIENT = '4899dfa7-2462-46f1-bc45-ad4802deb0fc';
export const PARTNER_CLIENT = 'a99938fb-c4f0-463a-aecb-6d16440039fe';
export const CUSTOMER_SUCCESS = 'b4195042-6669-4a45-a753-5400b7822fd7';

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(private http: HttpClient, private authService: AuthService) { }

  listAll(): Observable<User[]> {
    return this.http.get<User[]>(`${environment.apiUrl}/users`);
  }

  /**
   * Busca livre de usuário. O texto vai por `HttpParams`, que codifica o valor:
   * interpolado cru, `&` partia a busca em dois parâmetros, `+` virava espaço,
   * `#` truncava e `%` gerava escape inválido (CU-86akkudjd).
   */
  filter(text: string): Observable<User[]> {
    return this.http.get<User[]>(`${environment.apiUrl}/users`, {
      params: new HttpParams().set('text', text ?? ''),
    });
  }

  listPaginated(
    page: number,
    perPage: number,
    filter: UserFilter = null,
    order: string = 'created_at',
    inverted: boolean | number = false
  ): Observable<PaginatedResult<User[]>> {
    let url = `${environment.apiUrl}/users?page=${page}&per_page=${perPage}`;
    if (
      filter !== null &&
      (filter.text || filter.active !== null || filter.validated !== null || filter.promocode !== null || filter.promocodeId !== null)
    ) {
      url += `&filter=` + encodeURIComponent(JSON.stringify(filter));
    }

    if (order) {
      url += `&order=${order}`;
    }

    url += `&inverted=${inverted}`;


    return this.http.get<PaginatedResult<User[]>>(url);
  }

  listCreditsPaginated(page: number, perPage: number, startDate?: string, endDate?: string, profileId?: string): Observable<PaginatedResult<User[]>> {
    let url = `${environment.apiUrl}/credits-info?page=${page}&per_page=${perPage}`;
    if (startDate) {
      url += `&start=${startDate}`;
    }
    if (endDate) {
      url += `&end=${endDate}`;
    }
    if (profileId) {
      url += `&profile_id=${profileId}`;
    }
    return this.http.get<PaginatedResult<User[]>>(url);
  }

  search(document: string = '', email: string = ''): Observable<any> {
    let url = `${environment.apiUrl}/users/search`;
    if (document) {
      url += `?document=${encodeURIComponent(document)}`;
    } else if (email) {
      url += `?email=${encodeURIComponent(email)}`;
    }
    return this.http.get<any>(url)
  }

  signUp(user: User): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/sign-up`, user);
  }

  startPasswordReset(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/password-reset`, {
      email,
    });
  }

  setNewPassword(password: string, hash: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/users/password-reset/${hash}`,
      { password }
    );
  }

  verifyPasswordHash(hash: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/password-reset/${hash}`);
  }

  /**
   * Desliga o segundo fator de um usuário (CU-86ak84mj6). Só admin alcança, e a
   * operação só DESLIGA: ligar depende do aparelho da pessoa.
   */
  disableTwoFactor(userId: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/users/${userId}/two-factor`);
  }

  /**
   * Liga o segundo fator de outra conta e devolve a chave para repassar à
   * pessoa. É a única vez que este material sai do servidor — chamar de novo
   * gera outra chave e invalida a anterior (CU-86ak84mj6).
   */
  enableTwoFactor(userId: string): Observable<TwoFactorProvision> {
    return this.http.post<TwoFactorProvision>(
      `${environment.apiUrl}/users/${userId}/two-factor`, {}
    );
  }

  save(user: User): Observable<User> {
    const original_user = this.authService.getUser(true);
    const edit_user_object = { ...user };

    if (user.profile_id && original_user.profile_id != ADMINISTRATOR) {
      delete edit_user_object.profile_id;
    }

    if (user.id) {
      return this.http.put<User>(
        `${environment.apiUrl}/users/${user.id}`,
        edit_user_object
      );
    }

    return this.http.post<User>(`${environment.apiUrl}/users`, edit_user_object);
  }


  findById(id: string): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/users/${id}`);
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/users/${id}`);
  }

  validate(id: string): Observable<any> {
    return this.http.put(`${environment.apiUrl}/users/${id}/validate`, {});
  }

  /**
   * Histórico de edições da conta — quem alterou, quando e quais campos.
   * Restrito a administrador no backend.
   */
  listEditLogs(
    id: string,
    page: number = 1,
    perPage: number = 15
  ): Observable<PaginatedResult<UserEditLog[]>> {
    return this.http.get<PaginatedResult<UserEditLog[]>>(
      `${environment.apiUrl}/users/${id}/edit-logs?page=${page}&per_page=${perPage}`
    );
  }

  findAffiliates(
    page: number,
    perPage: number,
    filter: AgencyFilter = null,
    sort: 'name' | 'balance' = 'name',
    dir: 'asc' | 'desc' = 'asc'
  ): Observable<AffiliatesPage> {
    let url = `${environment.apiUrl}/users/affiliates?page=${page}&per_page=${perPage}&sort=${sort}&dir=${dir}`;
    if (filter) {
      url += '&filter=' + encodeURIComponent(JSON.stringify(filter));
    }
    return this.http.get<AffiliatesPage>(url);
  }

  getPartnerDashboard(): Observable<PartnerInfo> {
    return this.http.get<PartnerInfo>(
      `${environment.apiUrl}/users/partner/dashboard`
    );
  }

  getPendingCommission(): Observable<UserCommission> {
    return this.http.get<UserCommission>(`${environment.apiUrl}/users/partner/pending`);
  }

  getPayments(page: number, perPage: number): Observable<PaginatedResult<UserPayment[]>> {
    return this.http.get<PaginatedResult<UserPayment[]>>(
      `${environment.apiUrl}/users/partner/payments?page=${page}`
    );
  }

  getPendingCommissionsPaginate(page: number, per_page: number): Observable<PaginatedResult<UserCommission[]>> {
    return this.http.get<PaginatedResult<UserCommission[]>>(
      `${environment.apiUrl}/users/partner/pending?page=${page}`
    );
  }

  listWithoutReference(userId: string, filter: string): Observable<User[]> {
    return this.http.get<User[]>(`${environment.apiUrl}/users/${userId}/without-reference?filter=${encodeURIComponent(filter)}`);
  }

  listAlerts(): Observable<Notification[]> {
    return this.http.get<Notification[]>(`${environment.apiUrl}/users/alerts?no-loading=true`);
  }

  updateAlert(id: number) {
    return this.http.put(`${environment.apiUrl}/users/alerts/${id}`, {});
  }

  saveGlobalAccount(globalAccount: Twilio) {
    return this.http.put(`${environment.apiUrl}/users/global`, globalAccount);
  }

  getGlobalAccount(): Observable<Twilio> {
    return this.http.get<Twilio>(`${environment.apiUrl}/users/global`);
  }

  deleteGlobalAccount() {
    return this.http.delete(`${environment.apiUrl}/users/global`);
  }

  getOperationTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.baseUrl}/operation_types`);
  }

  generateApiToken(): Observable<ApiToken.View> {
    return this.http.post<ApiToken.View>(`${environment.apiUrl}/users/partners-api-token`, {});
  }
}
